/**
 * breadcrumbs.tsx — the system-topology chrome that sits between the nav and the
 * scrolling main view:
 *
 *   • <Breadcrumbs/> — a human-readable path to the entity the main view renders
 *     (Studio › Section › Entity). Each crumb carries a hover-only dropdown caret that
 *     lets you jump to a sibling (another section, or another session) without leaving.
 *   • <EntityPath/> — the bottom status bar: the same path rendered as URN-derived
 *     slugs (the eventual context-menu target; metadata/graph view is out of scope).
 *
 * Both read names through `resolveRef` — the "IDs never display" spine — so a raw URN
 * becomes a title here just as it does in queries and the assistant.
 */
import { useRoute, navigate } from "../lib/router.tsx";
import { useWorkspace } from "../lib/hooks.ts";
import { useT } from "../lib/i18n.tsx";
import { resolveRef, listRefs } from "../../domain/resolve.ts";
import type { Workspace } from "../../store/events.ts";

interface MenuItem {
  readonly label: string;
  readonly sublabel?: string;
  readonly to: string;
  readonly on?: boolean;
}
interface Crumb {
  readonly label: string;
  readonly to: string;
  readonly current?: boolean;
  readonly items?: readonly MenuItem[];
}

/** The pipeline sections, in nav order — the menu every section/Studio crumb offers. */
const SECTIONS: { to: string; key: string }[] = [
  { to: "/", key: "nav.studio" },
  { to: "/evidence", key: "nav.evidence" },
  { to: "/interpret", key: "nav.interpret" },
  { to: "/author", key: "nav.author" },
  { to: "/evaluate", key: "nav.evaluate" },
  { to: "/package", key: "nav.package" },
  { to: "/project", key: "nav.project" },
  { to: "/registry", key: "nav.registry" },
  { to: "/trace", key: "nav.trace" },
];

function sectionLabel(t: (k: string) => string, base: string): string {
  const s = SECTIONS.find((x) => x.to === base);
  return s ? t(s.key) : base.replace(/^\//, "");
}

/** Build the crumb trail for the current route, resolving any entity id to its name. */
function buildCrumbs(ws: Workspace | null, path: string, t: (k: string) => string): Crumb[] {
  const sectionMenu: MenuItem[] = SECTIONS.map((s) => ({ label: t(s.key), to: s.to, on: s.to === "/" ? path === "/" : path.startsWith(s.to) }));
  const studio: Crumb = { label: t("nav.studio"), to: "/", current: path === "/", items: sectionMenu };
  if (path === "/") return [studio];

  // A session is the one route that addresses a specific entity.
  if (path.startsWith("/session/")) {
    const id = path.slice("/session/".length);
    const ref = ws ? resolveRef(ws, id) : null;
    const siblings: MenuItem[] = ws
      ? listRefs(ws, "session").map((r) => ({ label: r.label, ...(r.sublabel ? { sublabel: r.sublabel } : {}), to: `/session/${r.id}`, on: r.id === id }))
      : [];
    return [
      studio,
      { label: "Sessions", to: "/", items: sectionMenu },
      { label: ref?.label ?? "session", to: path, current: true, items: siblings },
    ];
  }

  const base = "/" + (path.split("/").filter(Boolean)[0] ?? "");
  return [studio, { label: sectionLabel(t, base), to: base, current: true, items: sectionMenu }];
}

function CrumbView({ crumb }: { crumb: Crumb }): JSX.Element {
  return (
    <span className={`crumb ${crumb.current ? "current" : ""}`}>
      <button className="crumb-link" disabled={crumb.current} onClick={() => !crumb.current && navigate(crumb.to)} title={crumb.label}>
        {crumb.label}
      </button>
      {crumb.items && crumb.items.length > 0 ? (
        <>
          <button className="crumb-caret" aria-label="related" tabIndex={-1}>▾</button>
          <div className="crumb-menu" role="menu">
            {crumb.items.map((it) => (
              <button key={it.to} className={`crumb-mi ${it.on ? "on" : ""}`} role="menuitem" onClick={() => navigate(it.to)} title={it.sublabel ?? it.label}>
                {it.label}
                {it.sublabel ? <span className="crumb-mi-sub">{it.sublabel}</span> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </span>
  );
}

export function Breadcrumbs(): JSX.Element {
  const path = useRoute();
  const { ws } = useWorkspace();
  const t = useT();
  const crumbs = buildCrumbs(ws, path, t);
  return (
    <div className="crumbs">
      <div className="crumbs-inner">
        {crumbs.map((c, i) => (
          <span key={i} style={{ display: "contents" }}>
            {i > 0 ? <span className="crumb-sep" aria-hidden="true">/</span> : null}
            <CrumbView crumb={c} />
          </span>
        ))}
      </div>
    </div>
  );
}

/** kebab a label into a URL-ish slug for the bottom status path. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "·";
}

/** The bottom path: each route segment as a slug, ids resolved to a name then slugged. */
export function EntityPath(): JSX.Element {
  const path = useRoute();
  const { ws } = useWorkspace();
  const t = useT();
  const raw = path.split("/").filter(Boolean);
  const segs: string[] = ["studio"];
  for (let i = 0; i < raw.length; i++) {
    const part = raw[i]!;
    if (part.startsWith("urn:")) {
      segs.push(slugify(ws ? resolveRef(ws, part).label : part));
    } else if (i === 0) {
      segs.push(slugify(sectionLabel(t, "/" + part)));
    } else {
      segs.push(slugify(decodeURIComponent(part)));
    }
  }
  return (
    <footer className="app-foot">
      <span className="foot-path">
        {segs.map((s, i) => (
          <span key={i} style={{ display: "contents" }}>
            {i > 0 ? <span className="foot-sep" aria-hidden="true">/</span> : null}
            <span className={`foot-seg ${i === segs.length - 1 ? "current" : ""}`}>{s}</span>
          </span>
        ))}
      </span>
    </footer>
  );
}
